import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/*
 * "Back" that goes back one step.
 *
 * Every Back control on the portal used to be a Link to a FIXED page — the
 * shooter page always went to the rankings, the shot map always to the results.
 * A reader who arrived by Matches → Strings → Shooter pressed Back once and
 * landed three levels up, at the page they had started from, because that fixed
 * destination had nothing to do with where they actually came from.
 *
 * So this steps back through history instead. React Router keeps an index on
 * the history entry it created; when that index is zero — or missing, which is
 * what a page opened in a new tab or pasted into the address bar looks like —
 * there is nothing behind us, and the named fallback is used so the button is
 * never a dead end.
 */
export default function BackButton({
  to,
  label,
  className = "",
}: {
  /** Where to go when this page was opened directly, with no history behind it. */
  to: string;
  label: string;
  className?: string;
}) {
  const navigate = useNavigate();

  const hasHistory = (() => {
    try {
      return (window.history.state?.idx ?? 0) > 0;
    } catch {
      // A sandboxed frame can refuse history entirely; the fallback still works.
      return false;
    }
  })();

  const classes =
    className ||
    "inline-flex h-10 items-center gap-2 rounded-lg border border-line bg-white px-4 text-sm font-semibold text-ink transition-colors hover:bg-slate-50";

  if (!hasHistory) {
    return (
      <Link to={to} className={classes}>
        <ArrowLeft size={15} /> {label}
      </Link>
    );
  }

  return (
    <button onClick={() => navigate(-1)} className={classes}>
      <ArrowLeft size={15} /> Back
    </button>
  );
}
